import { kea } from "kea";
import api from '../utils/api';

import { assetmaplogic, assetinfowindowlogic } from './global';
import grouplogic from "./group";
import profilelogic from "./profile";
import checkRole from "src/utils/checkRole";

export const assetlogic = kea({
	actions: {
		fetchAll: true,
		update: (asset) => ({ asset }),
		updateGroup: (asset) => ({ asset }),
		fetchUpdatedPosition: (assets, timestamp) => ({ assets, timestamp }),
		fetchHistory: (asset) => ({ asset }),
		fetchReplay: (asset) => ({ asset }),
		updateImage: (asset) => ({ asset }),

		setAll: (assets) => ({ assets }),
		setOne: (asset) => ({ asset }),
		removeOne: (asset) => ({ asset }),
		setPosition: (assets) => ({ assets }),
		setHistory: (history) => ({ history }),
		setReplay: (replay) => ({ replay }),
		setError: (error) => ({ error }),
		setSuccess: (success) => ({ success }),

		onMapAdd: (asset) => ({ asset }),
		onMapRemove: (asset) => ({ asset }),
		setSelected: (asset) => ({ asset }),
	},
	selectors: {
		assetsOnMap: [
			(selectors) => [selectors.assets, selectors.onMap],
			(assets, onMap) => assets.filter(asset => onMap.includes(asset._id))
		],
		//TODO get selected
		selected: [
			(selectors) => [selectors.assets, selectors.selected],
			(assets, selected) => assets.find(asset => asset._id === selected)
		]
	},
	reducers: {
		assets: [null, {
			setAll: (_, { assets }) => assets,
			setOne: (state, { asset }) => state.map((s) => s._id === asset._id ? asset : s),
			removeOne: (state, { asset }) => state.filter((s) => asset._id !== s._id),
			setPosition: (state, { assets }) => state.map((outdated) => {
				for (const updated of assets) {
					if (updated._id === outdated._id) {
						outdated.beacon.positions = updated.beacon.positions;
						break;
					}
				}
				return outdated;
			})
		}],
		onMap: [[], {
			onMapAdd: (state, { asset }) => [...state, asset],
			onMapRemove: (state, { asset }) => state.filter((_id) => asset !== _id),
		}],
		selected: [null, {
			setSelected: (_, { asset }) => asset,
		}],
		history: [[], {
			setHistory: (_, { history }) => history,
		}],
		replay: [[], {
			setReplay: (_, { replay }) => replay,
		}],
		tripCounter: [null, {
			setTripCounter: (_, { counter }) => counter
		}],
		loading: [false, {
			create: () => true,
			fetchAll: () => true,
			update: () => true,
			remove: () => true,
			updateGroup: () => true,
			fetchUpdatedPosition: () => true,
			fetchHistory: () => true,
			fetchReplay: () => true,
			immobilise: () => true,
			doorlock: () => true,
			requestTripCounter: () => true,
			resetTripCounter: () => true,
			simStatus: () => true,
			updateImage: () => true,

			setAll: () => false,
			setOne: () => false,
			removeOne: () => false,
			setPosition: () => false,
			setHistory: () => false,
			setReplay: () => false,
			setError: () => false,
			setSuccess: () => false,
		}],
		error: [null, {
			setAll: () => null,
			setOne: () => null,
			removeOne: () => null,
			setPosition: () => null,
			setHistory: () => null,
			setReplay: () => null,
			setSuccess: () => null,
			setError: (_, { error }) => [error],
		}],
		success: [null, {
			setError: () => null,
			setSuccess: (_, { success }) => [success]
		}]
	},
	listeners: ({ actions }) => ({
		fetchAll: async (_, breakpoint) => {
			await breakpoint(300)
			let url = '/asset';
			let res;
			try {
				res = await api({ url });
			} catch (error) {
				actions.setError(error.message);
				return;
			}
			breakpoint();
			const json = await res.json();
			if (res.ok) actions.setAll(json.payload);
			else actions.setError(json.error);
		},
		update: async ({ asset }, breakpoint) => {
			await breakpoint(300);
			let url = `/asset/${asset._id}`;
			let res;
			try {
				res = await api({ url, method: 'put', body: asset });
			} catch (error) {
				actions.setError(error.message);
				return;
			}
			const json = await res.json();
			if (res.ok) {
				actions.setOne(json.payload);
				if (checkRole(profilelogic.values.profile, ['Admin'])) grouplogic.actions.fetchAll();
				actions.setSuccess('Asset Updated.');
			} else actions.setError(json.error);
		},
		updateGroup: async ({ asset }, breakpoint) => {
			await breakpoint(300);
			let url = `/asset/${asset._id}/group`;
			let res;
			try {
				res = await api({ url, method: 'put', body: asset });
			} catch (error) {
				actions.setError(error.message);
				return;
			}
			const json = await res.json();
			if (res.ok) {
				actions.setOne(json.payload)
				if (checkRole(profilelogic.values.profile, ['Admin'])) grouplogic.actions.fetchAll();
				actions.setSuccess('Asset Updated.');
			} else actions.setError(json.error);
		},
		fetchUpdatedPosition: async ({ assets, timestamp }, breakpoint) => {
			await breakpoint(300);
			let url = `/asset/update_location`;
			let res;
			try {
				res = await api({ url, method: 'post', body: { assets, timestamp } });
			} catch (error) {
				actions.setError(error.message);
				return;
			}
			const json = await res.json();
			if (res.ok) {
				actions.setPosition(json.payload);
				assetinfowindowlogic.actions.setPosition(json.payload);
				assetmaplogic.actions.setPosition(json.payload);
			} else actions.setError(json.error);
		},
		fetchHistory: async ({ asset }, breakpoint) => {
			await breakpoint(300);
			let url = `/asset/${asset._id}/history`;
			let res;
			try {
				res = await api({ url, method: 'post', body: asset });
			} catch (error) {
				actions.setError(error.message);
				return;
			}
			const json = await res.json();
			if (res.ok) {
				actions.setHistory(json.payload);
				actions.setSuccess('History Fetched.');
			} else actions.setError(json.error);
		},
		fetchReplay: async ({ asset }, breakpoint) => {
			await breakpoint(300);
			let url = `/asset/${asset._id}/replay`;
			let res;
			try {
				res = await api({ url, method: 'post', body: asset });
			} catch (error) {
				actions.setError(error.message);
				return;
			}
			const json = await res.json();
			if (res.ok) {
				actions.setReplay(json.payload);
				actions.setSuccess('Replay Fetched.');
			} else actions.setError(json.error);
		},
		updateImage: async ({ asset }, breakpoint) => {
			await breakpoint(300);
			let url = `/asset/${asset._id}/update_image`;
			let res;
			try {
				res = await api({ url, method: 'put', body: asset });
			} catch (error) {
				actions.setError(error.message);
				return;
			}
			const json = await res.json();
			if (res.ok) {
				actions.setOne(json.payload);
				if (checkRole(profilelogic.values.profile, ['Admin'])) grouplogic.actions.fetchAll();
				actions.setSuccess('Asset Updated.');
			} else actions.setError(json.error);
		},
	})
});

export default assetlogic;
